// core/modals/usermanagement/UserModal.jsx
import React, { useEffect, useState } from "react";
import Select from "react-select";
import PropTypes from 'prop-types';
import { userActions } from "../../action/userAction";
import { toast } from "react-toastify";

const UserModal = ({ mode, user, roles, onClose }) => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        role_id: "",
        department: "",
        designation: "",
        joining_date: "",
        gender: "",
        address: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        status: "active"
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Load user data if in edit/view mode
    useEffect(() => {
        if (user && (mode === 'edit' || mode === 'view')) {
            setFormData({
                name: user.name || "",
                email: user.email || "",
                phone: user.phone || "",
                password: "",
                confirmPassword: "",
                role_id: user.role_id || "",
                department: user.department || "",
                designation: user.designation || "",
                joining_date: user.joining_date || "",
                gender: user.gender || "",
                address: user.address || "",
                city: user.city || "",
                state: user.state || "",
                country: user.country || "",
                pincode: user.pincode || "",
                status: user.status || "active"
            });
        }
    }, [user, mode]);

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    // Handle select change
    const handleSelectChange = (name, option) => {
        setFormData(prev => ({ ...prev, [name]: option?.value || "" }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        if (mode === 'add') {
            if (!formData.password) {
                newErrors.password = 'Password is required';
            } else if (formData.password.length < 6) {
                newErrors.password = 'Password must be at least 6 characters';
            }

            if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
        }

        if (!formData.role_id) {
            newErrors.role_id = 'Role is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setLoading(true);

        const userData = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            role_id: formData.role_id,
            department: formData.department,
            designation: formData.designation,
            joining_date: formData.joining_date,
            gender: formData.gender,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            country: formData.country,
            pincode: formData.pincode,
            status: formData.status
        };

        if (mode === 'add') {
            userData.password = formData.password;
        }

        try {
            let result;

            if (mode === 'add') {
                result = await userActions.createUser(userData);
            } else if (mode === 'edit') {
                userData.id = user.id;
                result = await userActions.updateUser(userData);
            }

            if (result && result.success) {
                toast.success(result.message || `User ${mode === 'add' ? 'created' : 'updated'} successfully`);
                onClose(true);
            } else {
                toast.error(result?.message || `Failed to ${mode} user`);
            }
        } catch (error) {
            console.error(`Error ${mode}ing user:`, error);
            toast.error(`Failed to ${mode} user`);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    // Role options for select
    const roleOptions = roles.map(role => ({
        value: role.id,
        label: role.role_name
    }));

    // Gender options
    const genderOptions = [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' }
    ];

    // Status options
    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
    ];

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {mode === 'add' && 'Add User'}
                            {mode === 'edit' && 'Edit User'}
                            {mode === 'view' && 'User Details'}
                        </h5>
                        <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                {/* Name */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Full Name {mode !== 'view' && <span className="text-danger">*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter full name"
                                        disabled={mode === 'view'}
                                        required={mode !== 'view'}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>

                                {/* Email */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Email {mode !== 'view' && <span className="text-danger">*</span>}
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter email address"
                                        disabled={mode === 'view'}
                                        required={mode !== 'view'}
                                    />
                                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                </div>

                                {/* Phone */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Phone</label>
                                    <input
                                        type="text"
                                        name="phone"
                                        className="form-control"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        placeholder="Enter phone number"
                                        disabled={mode === 'view'}
                                    />
                                </div>

                                {/* Role */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Role {mode !== 'view' && <span className="text-danger">*</span>}
                                    </label>
                                    <Select
                                        options={roleOptions}
                                        value={roleOptions.find(opt => opt.value === formData.role_id)}
                                        onChange={(opt) => handleSelectChange('role_id', opt)}
                                        placeholder="Select role"
                                        isClearable
                                        isDisabled={mode === 'view'}
                                        required={mode !== 'view'}
                                    />
                                    {errors.role_id && <div className="text-danger small mt-1">{errors.role_id}</div>}
                                </div>

                                {/* Password - only for add mode */}
                                {mode === 'add' && (
                                    <>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">
                                                Password <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                name="password"
                                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                placeholder="Enter password (min 6 characters)"
                                                required
                                            />
                                            {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">
                                                Confirm Password <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                name="confirmPassword"
                                                className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                placeholder="Confirm password"
                                                required
                                            />
                                            {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                                        </div>
                                    </>
                                )}

                                {/* Department */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Department</label>
                                    <input
                                        type="text"
                                        name="department"
                                        className="form-control"
                                        value={formData.department}
                                        onChange={handleInputChange}
                                        placeholder="Enter department"
                                        disabled={mode === 'view'}
                                    />
                                </div>

                                {/* Designation */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Designation</label>
                                    <input
                                        type="text"
                                        name="designation"
                                        className="form-control"
                                        value={formData.designation}
                                        onChange={handleInputChange}
                                        placeholder="Enter designation"
                                        disabled={mode === 'view'}
                                    />
                                </div>

                                {/* Joining Date */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Joining Date</label>
                                    <input
                                        type="date"
                                        name="joining_date"
                                        className="form-control"
                                        value={formData.joining_date}
                                        onChange={handleInputChange}
                                        disabled={mode === 'view'}
                                    />
                                </div>

                                {/* Gender */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Gender</label>
                                    <Select
                                        options={genderOptions}
                                        value={genderOptions.find(opt => opt.value === formData.gender)}
                                        onChange={(opt) => handleSelectChange('gender', opt)}
                                        placeholder="Select gender"
                                        isClearable
                                        isDisabled={mode === 'view'}
                                    />
                                </div>

                                {/* Status */}
                                {mode !== 'add' && (
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Status</label>
                                        <Select
                                            options={statusOptions}
                                            value={statusOptions.find(opt => opt.value === formData.status)}
                                            onChange={(opt) => handleSelectChange('status', opt)}
                                            placeholder="Select status"
                                            isClearable
                                            isDisabled={mode === 'view'}
                                        />
                                    </div>
                                )}

                                {/* Address */}
                                <div className="col-12 mb-3">
                                    <label className="form-label">Address</label>
                                    <textarea
                                        name="address"
                                        className="form-control"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        placeholder="Enter address"
                                        rows="2"
                                        disabled={mode === 'view'}
                                    />
                                </div>

                                {/* City */}
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">City</label>
                                    <input
                                        type="text"
                                        name="city"
                                        className="form-control"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        placeholder="Enter city"
                                        disabled={mode === 'view'}
                                    />
                                </div>

                                {/* State */}
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">State</label>
                                    <input
                                        type="text"
                                        name="state"
                                        className="form-control"
                                        value={formData.state}
                                        onChange={handleInputChange}
                                        placeholder="Enter state"
                                        disabled={mode === 'view'}
                                    />
                                </div>

                                {/* Country */}
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Country</label>
                                    <input
                                        type="text"
                                        name="country"
                                        className="form-control"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                        placeholder="Enter country"
                                        disabled={mode === 'view'}
                                    />
                                </div>

                                {/* Pincode */}
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Pincode</label>
                                    <input
                                        type="text"
                                        name="pincode"
                                        className="form-control"
                                        value={formData.pincode}
                                        onChange={handleInputChange}
                                        placeholder="Enter pincode"
                                        disabled={mode === 'view'}
                                    />
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>
                            Cancel
                        </button>
                        {mode !== 'view' && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : mode === 'add' ? 'Add User' : 'Update User'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

UserModal.propTypes = {
    mode: PropTypes.oneOf(['add', 'edit', 'view']).isRequired,
    user: PropTypes.object,
    roles: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired
};

export default UserModal;